import React, { useState, useEffect, useRef } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';
import * as XLSX from 'xlsx';

const AdminStudentManagement = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // State for students data - CLIENT-SIDE FILTERING APPROACH
  const [allStudents, setAllStudents] = useState([]); // Store all students
  const [filteredStudents, setFilteredStudents] = useState([]); // Store filtered results
  const [displayedStudents, setDisplayedStudents] = useState([]); // Store paginated results
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true); // NEW: Track if this is initial load

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(10);

  // FILTERING STATES
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterBatch, setFilterBatch] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // DYNAMIC DEPARTMENT AND FILTER BATCH DATA (Only for filters, not for forms)
  const [departments, setDepartments] = useState([]);
  const [filterBatches, setFilterBatches] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [filterBatchesLoading, setFilterBatchesLoading] = useState(false);

  // Form state - UPDATED FOR DATABASE YEAR FORMAT WITH MANUAL BATCH INPUT
  const [formData, setFormData] = useState({
    student_id: '',
    username: '',
    name: '',
    father_name: '',
    dob: '',
    roll_number: '',
    reg_number: '',
    course_name: '',
    phone_number: '',
    email: '',
    present_address: '',
    dept: '',
    year: 2001, // Start with 2001 (1st year in database)
    parents_number: '',
    batch_name: '', // Now manual input, not from dropdown
    session: '',
    blood_group: '',
    admission_year: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Handle student profile photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Please select a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB.');
      return;
    }

    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const studentId = viewStudent?.student_id || viewStudent?.id;

      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await fetch(`${API_LINK}/api/admin/students/${studentId}/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        alert('Photo uploaded successfully!');
        // Update viewStudent locally with new image path
        setViewStudent(prev => ({ ...prev, image_path: result.data.image_path }));
        // Refresh the student list
        setInitialLoad(false);
        await fetchAllStudents();
      } else {
        alert('Failed to upload photo: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      alert('Error uploading photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  // Year options
  const yearOptions = [
    { value: 1, label: '1st Year' },
    { value: 2, label: '2nd Year' },
    { value: 3, label: '3rd Year' },
    { value: 4, label: '4th Year' }
  ];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API HELPER FUNCTION
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // FETCH DEPARTMENTS FROM DATABASE
  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const result = await apiCall('/departments');

      if (result.success) {
        setDepartments(result.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError('Failed to fetch departments: ' + error.message);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // FETCH FILTER BATCHES (FOR FILTERING ONLY - NOT FOR FORMS)
  const fetchFilterBatches = async (department, year) => {
    if (!department || !year) {
      setFilterBatches([]);
      return;
    }

    try {
      setFilterBatchesLoading(true);
      console.log('Fetching batches for filter:', { department, year });
      const result = await apiCall(`/batches?department=${encodeURIComponent(department)}&year=${year}`);

      if (result.success && result.data) {
        console.log('Filter batches loaded:', result.data);
        setFilterBatches(result.data);
      } else {
        console.log('No batches found for filter');
        setFilterBatches([]);
      }
    } catch (error) {
      console.error('Error fetching filter batches:', error);
      setFilterBatches([]);
      setError('Failed to fetch filter batches: ' + error.message);
    } finally {
      setFilterBatchesLoading(false);
    }
  };

  // FETCH INITIAL DATA
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Fetch ALL students from backend (CLIENT-SIDE FILTERING APPROACH)
  const fetchAllStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      // Fetch a large number of students
      const apiUrl = `${API_LINK}/api/admin/students?page=1&limit=50000`;

      console.log('Fetching all students from API...');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Admin API endpoint not found. Please check if the backend server is running correctly.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred. Please check the backend logs.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setError('Server returned invalid response format. Expected JSON.');
        return;
      }

      const result = await response.json();
      console.log('API Response:', result);

      if (result.success) {
        const studentsData = result.data?.students || [];

        // Store all students
        setAllStudents(studentsData);

        console.log(`Loaded ${studentsData.length} students from server`);

        // CHANGED: Don't show data initially, only after filters are applied
        if (initialLoad) {
          // On initial load, don't display any data
          setFilteredStudents([]);
          setDisplayedStudents([]);
          setTotalCount(0);
          setTotalPages(0);
          setCurrentPage(1);
          setInitialLoad(false);
        } else {
          // If not initial load (e.g., refresh), apply current filters
          applyFilters(studentsData, searchTerm, filterDepartment, filterYear, filterBatch);
        }

      } else {
        setError(result.message || 'Failed to fetch students from server');
      }
    } catch (error) {
      console.error('Fetch students error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
      } else if (error.message.includes('JSON')) {
        setError('Server returned invalid data format. Please check backend API.');
      } else {
        setError(`Failed to load students: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering function
  const applyFilters = (students, searchText, department, year, batch) => {
    let filtered = [...students];

    // Apply search filter (case-insensitive)
    if (searchText && searchText.trim()) {
      const searchLower = searchText.trim().toLowerCase();
      filtered = filtered.filter(student =>
        (student.name || '').toLowerCase().includes(searchLower) ||
        (student.student_id || '').toLowerCase().includes(searchLower) ||
        (student.email || '').toLowerCase().includes(searchLower) ||
        (student.username || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply department filter
    if (department && department.trim()) {
      filtered = filtered.filter(student =>
        student.dept === department
      );
    }

    // Apply year filter - UPDATED FOR DATABASE FORMAT (2001, 2002, 2003, 2004)
    if (year) {
      const dbYear = 2000 + parseInt(year); // Convert 1,2,3,4 to 2001,2002,2003,2004
      filtered = filtered.filter(student =>
        student.year === dbYear
      );
    }

    // Apply batch filter
    if (batch && batch.trim()) {
      filtered = filtered.filter(student =>
        student.batch_name === batch
      );
    }

    console.log(`Filtered ${filtered.length} students from ${students.length} total`);

    // Update filtered students
    setFilteredStudents(filtered);
    setTotalCount(filtered.length);

    // Calculate pagination
    const totalPagesCalc = Math.ceil(filtered.length / limit);
    setTotalPages(totalPagesCalc);

    // Reset to first page when filters change
    setCurrentPage(1);

    // Apply pagination to filtered results
    applyPagination(filtered, 1);
  };

  // Client-side pagination function
  const applyPagination = (filteredData, page) => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setDisplayedStudents(paginatedData);
    setCurrentPage(page);

    console.log(`Showing page ${page}: ${paginatedData.length} students (${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${filteredData.length})`);
  };

  // Initial data fetch
  useEffect(() => {
    fetchAllStudents();
  }, []);

  // FILTER HANDLERS FOR CLIENT-SIDE FILTERING
  const handleFilterDepartmentChange = (value) => {
    setFilterDepartment(value);
    setFilterYear('');
    setFilterBatch('');
    setFilterBatches([]);
  };

  const handleFilterYearChange = (value) => {
    const yearValue = value ? parseInt(value) : '';
    setFilterYear(yearValue);
    setFilterBatch('');

    if (yearValue && filterDepartment) {
      // Convert UI year (1,2,3,4) to database year (2001,2002,2003,2004) for batch API
      const dbYear = 2000 + yearValue;
      fetchFilterBatches(filterDepartment, dbYear);
    } else {
      setFilterBatches([]);
    }
  };

  const handleFilterBatchChange = (value) => {
    setFilterBatch(value);
  };

  const handleClearFilters = () => {
    setFilterDepartment('');
    setFilterYear('');
    setFilterBatch('');
    setFilterBatches([]);
    setSearchTerm('');

    // Clear displayed data when filters are cleared
    setFilteredStudents([]);
    setDisplayedStudents([]);
    setTotalCount(0);
    setTotalPages(0);
    setCurrentPage(1);
  };

  // Handle search and filter changes - INSTANT FILTERING
  useEffect(() => {
    if (allStudents.length > 0) {
      // Only apply filters if at least one filter is active
      const hasActiveFilters = searchTerm.trim() || filterDepartment || filterYear || filterBatch;

      if (hasActiveFilters) {
        console.log('Auto-applying filters:', { searchTerm, filterDepartment, filterYear, filterBatch });
        applyFilters(allStudents, searchTerm, filterDepartment, filterYear, filterBatch);
      } else {
        // No active filters, clear display
        setFilteredStudents([]);
        setDisplayedStudents([]);
        setTotalCount(0);
        setTotalPages(0);
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filterDepartment, filterYear, filterBatch, allStudents]);

  // Handle pagination changes
  const handlePageChange = (page) => {
    if (page && page >= 1 && page <= totalPages) {
      applyPagination(filteredStudents, page);
    }
  };

  // Handle search manually (for search button)
  const handleSearch = () => {
    console.log('Manual search triggered with term:', searchTerm);
    if (allStudents.length > 0) {
      applyFilters(allStudents, searchTerm, filterDepartment, filterYear, filterBatch);
    }
  };

  // Refresh students (re-fetch from server)
  const refreshStudents = () => {
    setInitialLoad(false); // Don't treat this as initial load
    fetchAllStudents();
  };

  // STUDENT FORM HANDLERS - UPDATED FOR MANUAL BATCH INPUT
  const handleFormDepartmentChange = (value) => {
    console.log('Form department changed to:', value);
    setFormData({ ...formData, dept: value });
    // Batch name is now manual input, no need to fetch batches for form
  };

  const handleFormYearChange = (value) => {
    console.log('Form year changed to:', value);
    // Value is already the database year format (2001, 2002, etc.)
    const dbYear = parseInt(value);
    setFormData({ ...formData, year: dbYear });
    // Batch name is now manual input, no need to fetch batches for form
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.student_id.trim()) errors.student_id = 'Student ID is required';
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.roll_number.trim()) errors.roll_number = 'Roll number is required';
    if (!formData.reg_number.trim()) errors.reg_number = 'Registration number is required';
    if (!formData.course_name.trim()) errors.course_name = 'Course name is required';
    if (!formData.phone_number.trim()) errors.phone_number = 'Phone number is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.present_address.trim()) errors.present_address = 'Address is required';
    if (!formData.dept.trim()) errors.dept = 'Department is required';
    if (!formData.parents_number.trim()) errors.parents_number = 'Parent number is required';
    if (!formData.batch_name.trim()) errors.batch_name = 'Batch name is required';

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email format is invalid';
    }

    // Phone validation
    if (formData.phone_number && !/^\d{10}$/.test(formData.phone_number)) {
      errors.phone_number = 'Phone number must be 10 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission for add/edit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = selectedStudent
        ? `${API_LINK}/api/admin/students/${selectedStudent.id}`
        : `${API_LINK}/api/admin/students`;

      const method = selectedStudent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();

        // Refresh all students after successful add/edit
        setInitialLoad(false); // Don't treat this as initial load
        await fetchAllStudents();

        alert(selectedStudent ? 'Student updated successfully!' : 'Student added successfully!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert('Failed to save student. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedStudent) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/students/${selectedStudent.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedStudent(null);

        // Refresh all students after successful delete
        setInitialLoad(false); // Don't treat this as initial load
        await fetchAllStudents();

        alert('Student deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert('Failed to delete student. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form - UPDATED FOR DATABASE YEAR FORMAT WITH MANUAL BATCH INPUT
  const resetForm = () => {
    setFormData({
      student_id: '',
      username: '',
      name: '',
      father_name: '',
      dob: '',
      roll_number: '',
      reg_number: '',
      course_name: '',
      phone_number: '',
      email: '',
      present_address: '',
      dept: '',
      year: 2001, // Reset to 2001 (1st year in database)
      parents_number: '',
      batch_name: '', // Now manual input
      session: '',
      blood_group: '',
      admission_year: ''
    });
    setFormErrors({});
    setSelectedStudent(null);
  };

  // Handle edit click - UPDATED FOR MANUAL BATCH INPUT
  const handleEditClick = (student) => {
    setSelectedStudent(student);
    setFormData({
      student_id: student.student_id || '',
      username: student.username || '',
      name: student.name || '',
      father_name: student.father_name || '',
      dob: formatDobForInput(student.dob),
      roll_number: student.roll_number || '',
      reg_number: student.reg_number || '',
      course_name: student.course_name || '',
      phone_number: student.phone_number || '',
      email: student.email || '',
      present_address: student.present_address || '',
      dept: student.dept || '',
      year: student.year || 2001, // Use database year format (2001, 2002, etc.)
      parents_number: student.parents_number?.toString() || '',
      batch_name: student.batch_name || '',
      session: student.session || '',
      blood_group: student.blood_group || '',
      admission_year: student.admission_year || ''
    });

    // No need to fetch batches since batch name is now manual input

    setShowEditModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (student) => {
    setSelectedStudent(student);
    setShowDeleteModal(true);
  };



  // Get year display - UPDATED FOR DATABASE FORMAT
  const getYearDisplay = (year) => {
    // Convert database year (2001, 2002, 2003, 2004) to display format
    const yearMap = {
      2001: '1st Year',
      2002: '2nd Year',
      2003: '3rd Year',
      2004: '4th Year'
    };
    return yearMap[year] || `Year ${year}`;
  };

  // Format DOB from ISO/any format to dd/mm/yyyy for display
  const formatDobForDisplay = (dob) => {
    if (!dob) return '';
    // If already in dd/mm/yyyy format, return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) return dob;
    try {
      const date = new Date(dob);
      if (isNaN(date.getTime())) return dob;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dob;
    }
  };

  // Format DOB for HTML date input (yyyy-mm-dd)
  const formatDobForInput = (dob) => {
    if (!dob) return '';
    // If in dd/mm/yyyy format, convert to yyyy-mm-dd
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
      const [day, month, year] = dob.split('/');
      return `${year}-${month}-${day}`;
    }
    // If ISO format, extract date part
    try {
      const date = new Date(dob);
      if (isNaN(date.getTime())) return '';
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    } catch {
      return '';
    }
  };

  // Convert date input value (yyyy-mm-dd) to dd/mm/yyyy for storage
  const formatDobForStorage = (inputValue) => {
    if (!inputValue) return '';
    // Input type="date" gives yyyy-mm-dd
    const parts = inputValue.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return inputValue;
  };

  // Handle Excel export
  const handleExportExcel = () => {
    // Use filtered students if filters are active, otherwise all students
    const dataToExport = filteredStudents.length > 0 ? filteredStudents : allStudents;

    if (dataToExport.length === 0) {
      alert('No student data to export. Please load or filter students first.');
      return;
    }

    // Prepare data for Excel
    const excelData = dataToExport.map((student, index) => ({
      'S.No': index + 1,
      'Student ID': student.student_id || '',
      'Name': student.name || '',
      'Department': student.dept || '',
      'Year': getYearDisplay(student.year),
      'Batch': student.batch_name || '',
      'Roll No': student.roll_number || '',
      'Reg No': student.reg_number || '',
      'Course': student.course_name || '',
      'DOB': formatDobForDisplay(student.dob),
      'Gender': student.gender || '',
      'Blood Group': student.blood_group || '',
      'Phone': student.phone_number || '',
      'Email': student.email || '',
      'Parent Phone': student.parents_number || '',
      'Address': student.present_address || ''
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const colWidths = Object.keys(excelData[0]).map(key => ({
      wch: Math.max(key.length, ...excelData.map(row => String(row[key] || '').length))
    }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Students');

    // Generate filename with filters info
    let filename = 'Student_Data';
    if (filterDepartment) filename += `_${filterDepartment}`;
    if (filterYear) filename += `_Year${filterYear}`;
    if (filterBatch) filename += `_${filterBatch}`;
    filename += `_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm.trim() || filterDepartment || filterYear || filterBatch;

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-students" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Student Management Portal 👥
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage all student records, add new students, and filter by department and batch.
        </p>
      </div>

      {/* FILTERS SECTION - INSTANT FILTERING */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>🔍</span>
          <h3 style={{ margin: 0, fontSize: '1.3rem', color: '#333' }}>
            Filter Students (Required to View Data)
          </h3>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Department Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: '#374151',
              fontSize: '0.9rem'
            }}>
              Department
            </label>
            <select
              value={filterDepartment}
              onChange={(e) => handleFilterDepartmentChange(e.target.value)}
              disabled={departmentsLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.9rem',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                backgroundColor: departmentsLoading ? '#f9fafb' : '#ffffff',
                cursor: departmentsLoading ? 'not-allowed' : 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#238548'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="">
                {departmentsLoading ? 'Loading...' : 'Select Department'}
              </option>
              {departments.map(dept => (
                <option key={dept.value} value={dept.value}>
                  {dept.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: '#374151',
              fontSize: '0.9rem'
            }}>
              Year
            </label>
            <select
              value={filterYear}
              onChange={(e) => handleFilterYearChange(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.9rem',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                backgroundColor: '#ffffff',
                cursor: 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#238548'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="">Select Year</option>
              {yearOptions.map(year => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
          </div>

          {/* Batch Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: '#374151',
              fontSize: '0.9rem'
            }}>
              Batch
            </label>
            <select
              value={filterBatch}
              onChange={(e) => handleFilterBatchChange(e.target.value)}
              disabled={!filterDepartment || !filterYear || filterBatchesLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.9rem',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                backgroundColor: (!filterDepartment || !filterYear) ? '#f9fafb' : '#ffffff',
                cursor: (filterDepartment && filterYear && !filterBatchesLoading) ? 'pointer' : 'not-allowed'
              }}
              onFocus={(e) => {
                if (filterDepartment && filterYear && !filterBatchesLoading) {
                  e.target.style.borderColor = '#238548';
                }
              }}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="">
                {filterBatchesLoading ? 'Loading...' :
                  !filterDepartment || !filterYear ? 'Select dept & year first' :
                    'Select Batch (Optional)'}
              </option>
              {filterBatches.map(batch => (
                <option key={batch} value={batch}>
                  {batch}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear Filters Button */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <button
            onClick={handleClearFilters}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: '#6b7280',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.backgroundColor = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <span>🗑️</span>
            Clear All Filters
          </button>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#0369a1'
            }}>
              <span>📌</span>
              Active Filters:
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
              {searchTerm.trim() && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#7c3aed',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  Search: {searchTerm.trim()}
                </span>
              )}
              {filterDepartment && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#1e40af',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  Dept: {departments.find(d => d.value === filterDepartment)?.label || filterDepartment}
                </span>
              )}
              {filterYear && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  Year: {yearOptions.find(y => y.value == filterYear)?.label || filterYear}
                </span>
              )}
              {filterBatch && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#059669',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  Batch: {filterBatch}
                </span>
              )}
            </div>
          </div>
        )}

        {/* No Filter Warning */}
        {!hasActiveFilters && !loading && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
            <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
              Please select at least one filter (Department, Year, Batch, or use Search) to view student data.
            </p>
          </div>
        )}
      </div>

      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Search Bar */}
          <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '400px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by name, student ID, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  width: '100%',
                  padding: '1rem 3rem 1rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#238548';
                  e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={handleSearch}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem'
                }}
              >
                🔍
              </button>
            </div>
          </div>

          {/* Add Student Button */}
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>+</span>
            Add New Student
          </button>

          {/* Refresh Button */}
          <button
            onClick={refreshStudents}
            disabled={loading}
            style={{
              padding: '1rem',
              background: '#f0f9ff',
              color: '#0369a1',
              border: '2px solid #e0f2fe',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#0369a1';
                e.target.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#f0f9ff';
                e.target.style.color = '#0369a1';
              }
            }}
            title="Refresh students"
          >
            {loading ? (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #0369a1',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : (
              '🔄'
            )}
          </button>

          {/* Export Excel Button */}
          <button
            onClick={handleExportExcel}
            disabled={loading || (allStudents.length === 0)}
            style={{
              padding: '1rem',
              background: '#f0fdf4',
              color: '#16a34a',
              border: '2px solid #bbf7d0',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: (loading || allStudents.length === 0) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              opacity: (loading || allStudents.length === 0) ? 0.6 : 1,
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              if (!loading && allStudents.length > 0) {
                e.target.style.backgroundColor = '#16a34a';
                e.target.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && allStudents.length > 0) {
                e.target.style.backgroundColor = '#f0fdf4';
                e.target.style.color = '#16a34a';
              }
            }}
            title={filteredStudents.length > 0 ? `Export ${filteredStudents.length} filtered students` : `Export all ${allStudents.length} students`}
          >
            📥 Export Excel
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading && allStudents.length === 0 ? 'Loading students...' :
              error ? 'Error loading students' :
                hasActiveFilters ? `Showing ${displayedStudents.length} of ${totalCount} students (filtered from ${allStudents.length} total)` :
                  `${allStudents.length} students loaded - Use filters to view data`}
          </span>
          {loading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Students</p>
          <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
          <button
            onClick={refreshStudents}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Students Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading && allStudents.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading students from database...</p>
          </div>
        ) : displayedStudents.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>👥</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {hasActiveFilters ? 'No students found matching your filters' : 'Ready to view student data'}
            </h3>
            <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
              {hasActiveFilters
                ? 'Try adjusting your search criteria or filters to find students'
                : 'Apply filters above (Department, Year, Batch, or Search) to view student records'
              }
            </p>
            {!hasActiveFilters && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#e0f2fe',
                borderRadius: '8px',
                margin: '0 auto',
                maxWidth: '500px'
              }}>
                <p style={{ margin: 0, color: '#0369a1', fontSize: '0.9rem' }}>
                  <strong>📋 How to view students:</strong><br />
                  1. Select a Department from the filter above<br />
                  2. Choose a Year (optional)<br />
                  3. Pick a Batch (optional)<br />
                  4. Or simply type in the Search box
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Student ID</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Name</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Batch Name</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Course</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Department</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Year</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Email</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedStudents.map((student, index) => (
                    <tr key={student.id} style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.3s ease'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem', fontWeight: 500, color: '#238548' }}>
                        {student.student_id}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: 500, color: '#333', marginBottom: '0.25rem' }}>
                            {student.name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            @{student.username}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}>
                          {student.batch_name || 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        {student.course_name}
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'uppercase'
                        }}>
                          {student.dept}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#fff3e0',
                          color: '#f57c00',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}>
                          {getYearDisplay(student.year)}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        {student.email}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => {
                              setViewStudent(student);
                              setShowViewModal(true);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#f3e8ff',
                              color: '#7c3aed',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#7c3aed';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#f3e8ff';
                              e.target.style.color = '#7c3aed';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={() => handleEditClick(student)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#1976d2';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#e3f2fd';
                              e.target.style.color = '#1976d2';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(student)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#ffebee',
                              color: '#d32f2f',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#d32f2f';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ffebee';
                              e.target.style.color = '#d32f2f';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount} total students (filtered from {allStudents.length})
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>


      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedStudent ? '✏️ Edit Student' : '➕ Add New Student'}
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                {selectedStudent ? 'Update student information' : 'Enter new student details'}
              </p>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                {/* Student ID */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Student ID *
                  </label>
                  <input
                    type="text"
                    value={formData.student_id}
                    onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                    placeholder="e.g., svu-2024-001-cs"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.student_id ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.student_id ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.student_id) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.student_id) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.student_id && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.student_id}
                    </p>
                  )}
                </div>

                {/* Username */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Username *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Enter username"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.username ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.username ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.username) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.username) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.username && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.username}
                    </p>
                  )}
                </div>

                {/* Name */}
                <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter student's full name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.name ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.name ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.name) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.name) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.name && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.name}
                    </p>
                  )}
                </div>

                {/* Date of Birth */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Roll Number */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Roll Number *
                  </label>
                  <input
                    type="text"
                    value={formData.roll_number}
                    onChange={(e) => setFormData({ ...formData, roll_number: e.target.value })}
                    placeholder="Enter roll number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.roll_number ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.roll_number ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.roll_number) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.roll_number) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.roll_number && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.roll_number}
                    </p>
                  )}
                </div>

                {/* Registration Number */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Registration Number *
                  </label>
                  <input
                    type="text"
                    value={formData.reg_number}
                    onChange={(e) => setFormData({ ...formData, reg_number: e.target.value })}
                    placeholder="Enter registration number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.reg_number ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.reg_number ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.reg_number) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.reg_number) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.reg_number && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.reg_number}
                    </p>
                  )}
                </div>

                {/* Course Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Course Name *
                  </label>
                  <input
                    type="text"
                    value={formData.course_name}
                    onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
                    placeholder="Enter course name (e.g., BCA, B.Tech CSE, Diploma in CS)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.course_name ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.course_name ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.course_name) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.course_name) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.course_name && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.course_name}
                    </p>
                  )}
                </div>

                {/* Department */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Department *
                  </label>
                  <select
                    value={formData.dept}
                    onChange={(e) => handleFormDepartmentChange(e.target.value)}
                    disabled={departmentsLoading}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.dept ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.dept ? '#fef2f2' : '#ffffff',
                      cursor: departmentsLoading ? 'not-allowed' : 'pointer'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.dept) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.dept) e.target.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <option value="">
                      {departmentsLoading ? 'Loading departments...' : 'Select Department'}
                    </option>
                    {departments.map(dept => (
                      <option key={dept.value} value={dept.value}>{dept.label}</option>
                    ))}
                  </select>
                  {formErrors.dept && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.dept}
                    </p>
                  )}
                </div>

                {/* Year - UPDATED FOR DATABASE YEAR FORMAT */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Year
                  </label>
                  <select
                    value={formData.year}
                    onChange={(e) => handleFormYearChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  >
                    <option value={2001}>1st Year</option>
                    <option value={2002}>2nd Year</option>
                    <option value={2003}>3rd Year</option>
                    <option value={2004}>4th Year</option>
                  </select>
                </div>

                {/* Batch Name - NOW MANUAL INPUT */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Batch Name *
                  </label>
                  <input
                    type="text"
                    value={formData.batch_name}
                    onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                    placeholder="Enter batch name (e.g., A, B, Morning, Evening)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.batch_name ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.batch_name ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.batch_name) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.batch_name) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.batch_name && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.batch_name}
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="10-digit phone number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.phone_number ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.phone_number ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.phone_number) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.phone_number) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.phone_number && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.phone_number}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="student@example.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.email ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.email ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.email) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.email) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.email && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.email}
                    </p>
                  )}
                </div>

                {/* Parent Number */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Parent/Guardian Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.parents_number}
                    onChange={(e) => setFormData({ ...formData, parents_number: e.target.value })}
                    placeholder="10-digit phone number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.parents_number ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.parents_number ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.parents_number) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.parents_number) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.parents_number && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.parents_number}
                    </p>
                  )}
                </div>

                {/* Father/Parent Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Father/Parent Name
                  </label>
                  <input
                    type="text"
                    value={formData.father_name}
                    onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                    placeholder="Enter father/parent name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: '#ffffff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Session */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Session
                  </label>
                  <input
                    type="text"
                    value={formData.session}
                    onChange={(e) => setFormData({ ...formData, session: e.target.value })}
                    placeholder="e.g. 2025-2026"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: '#ffffff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Blood Group */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Blood Group
                  </label>
                  <select
                    value={formData.blood_group}
                    onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: '#ffffff',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                {/* Admission Year */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Admission Year
                  </label>
                  <input
                    type="text"
                    value={formData.admission_year}
                    onChange={(e) => setFormData({ ...formData, admission_year: e.target.value })}
                    placeholder="e.g. 2025"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: '#ffffff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Address */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Present Address *
                  </label>
                  <textarea
                    value={formData.present_address}
                    onChange={(e) => setFormData({ ...formData, present_address: e.target.value })}
                    placeholder="Enter complete address with city, state, and pin code"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.present_address ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.present_address ? '#fef2f2' : '#ffffff',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.present_address) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.present_address) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.present_address && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.present_address}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              backgroundColor: '#f8fafc'
            }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Saving...
                  </>
                ) : (
                  <>
                    {selectedStudent ? '💾 Update Student' : '➕ Add Student'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                🗑️
              </div>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#dc2626'
              }}>
                Delete Student
              </h3>
              <p style={{
                margin: '0 0 1rem 0',
                color: '#6b7280',
                lineHeight: 1.6
              }}>
                Are you sure you want to delete <strong style={{ color: '#374151' }}>{selectedStudent.name}</strong>?
              </p>
              <p style={{
                margin: 0,
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                This action cannot be undone and will permanently remove all student data.
              </p>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem 2rem 2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedStudent(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  '🗑️ Delete Student'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Student Detail Modal */}
      {showViewModal && viewStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  👤 Student Details
                </h3>
                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                  {viewStudent.name || 'N/A'} — {viewStudent.student_id || 'N/A'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewStudent(null);
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.35)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Profile Photo Section */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '120px',
                  height: '140px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '3px solid #e9d5ff',
                  boxShadow: '0 4px 15px rgba(124, 58, 237, 0.15)',
                  position: 'relative',
                  background: '#f3e8ff'
                }}>
                  {(() => {
                    const photoUrl = viewStudent.image_path || viewStudent.photo_path || viewStudent.photo_url || viewStudent.photo || viewStudent.image || viewStudent.profile_image || viewStudent.profileImage;

                    const getFullUrl = (p) => {
                      if (!p) return null;
                      if (p.startsWith('http') || p.startsWith('blob:')) return p;
                      let cleanPath = p.startsWith('/') ? p.substring(1) : p;
                      return `${API_LINK}/${cleanPath}`;
                    };

                    return photoUrl ? (
                      <>
                        <style>{`
                          @keyframes viewShimmer {
                            0% { background-position: -200% 0; }
                            100% { background-position: 200% 0; }
                          }
                        `}</style>
                        <div className="photo-shimmer-view" style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          background: 'linear-gradient(90deg, #e9d5ff 25%, #f3e8ff 50%, #e9d5ff 75%)',
                          backgroundSize: '200% 100%',
                          animation: 'viewShimmer 1.5s infinite',
                          zIndex: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <span style={{ fontSize: '1.5rem', opacity: 0.4 }}>📷</span>
                        </div>
                        <img
                          src={getFullUrl(photoUrl)}
                          alt={viewStudent.name || 'Student'}
                          style={{
                            width: '100%', height: '100%', objectFit: 'cover',
                            position: 'relative', zIndex: 2,
                            transition: 'opacity 0.3s'
                          }}
                          onLoad={(e) => {
                            const shimmer = e.target.previousSibling;
                            if (shimmer) shimmer.style.display = 'none';
                          }}
                          onError={(e) => {
                            const currentSrc = e.target.src;
                            const filename = photoUrl.split('/').pop();
                            const tryUploads = `${API_LINK}/uploads/${filename}`;
                            const tryProfiles = `${API_LINK}/uploads/profiles/${filename}`;
                            const tryLegacy = `${API_LINK}/svu/uploads/profile_images/${filename}`;

                            if (!e.target.dataset.triedUploads) {
                              e.target.src = tryUploads;
                              e.target.dataset.triedUploads = 'true';
                            } else if (!e.target.dataset.triedProfiles) {
                              e.target.src = tryProfiles;
                              e.target.dataset.triedProfiles = 'true';
                            } else if (!e.target.dataset.triedLegacy) {
                              e.target.src = tryLegacy;
                              e.target.dataset.triedLegacy = 'true';
                            } else {
                              // All retries failed - show fallback
                              e.target.style.display = 'none';
                              const shimmer = e.target.previousSibling;
                              if (shimmer) {
                                shimmer.style.animation = 'none';
                                shimmer.style.background = '#f3e8ff';
                                shimmer.innerHTML = `<span style="font-size:2.5rem;opacity:0.5">👤</span>`;
                              }
                            }
                          }}
                        />
                      </>
                    ) : (
                      <div style={{
                        width: '100%', height: '100%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'linear-gradient(135deg, #e9d5ff 0%, #c4b5fd 100%)',
                        color: '#7c3aed', fontSize: '2.5rem', fontWeight: 700
                      }}>
                        {(viewStudent.name || '?').charAt(0).toUpperCase()}
                      </div>
                    );
                  })()}
                </div>
                {/* Upload / Change Photo Button */}
                <input
                  type="file"
                  ref={photoInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                />
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  style={{
                    marginTop: '0.35rem',
                    padding: '0.15rem 0.5rem',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.6rem',
                    fontWeight: 500,
                    cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                    background: 'transparent',
                    color: uploadingPhoto ? '#aaa' : '#7c3aed',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px'
                  }}
                  onMouseEnter={(e) => { if (!uploadingPhoto) e.currentTarget.style.color = '#5b21b6'; }}
                  onMouseLeave={(e) => { if (!uploadingPhoto) e.currentTarget.style.color = '#7c3aed'; }}
                >
                  {uploadingPhoto ? '⏳ Uploading...' : (viewStudent.image_path || viewStudent.photo_path || viewStudent.photo_url || viewStudent.photo || viewStudent.image || viewStudent.profile_image) ? '📷 Change Photo' : '📤 Upload Photo'}
                </button>
              </div>

              {/* Personal Information */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#7c3aed',
                  borderBottom: '2px solid #f3e8ff',
                  paddingBottom: '0.5rem'
                }}>
                  📋 Personal Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                  {[
                    { label: 'Full Name', value: viewStudent.name },
                    { label: 'Father\'s Name', value: viewStudent.father_name },
                    { label: 'Date of Birth', value: formatDobForDisplay(viewStudent.dob) },
                    { label: 'Gender', value: viewStudent.gender },
                    { label: 'Blood Group', value: viewStudent.blood_group },
                    { label: 'Username', value: viewStudent.username }
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      padding: '0.75rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #f3f4f6'
                    }}>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '1rem', color: '#1f2937', fontWeight: 500 }}>
                        {item.value || 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Academic Information */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#0369a1',
                  borderBottom: '2px solid #e0f2fe',
                  paddingBottom: '0.5rem'
                }}>
                  🎓 Academic Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                  {[
                    { label: 'Student ID', value: viewStudent.student_id },
                    { label: 'Roll Number', value: viewStudent.roll_number },
                    { label: 'Registration Number', value: viewStudent.reg_number },
                    { label: 'Department', value: viewStudent.dept },
                    { label: 'Course', value: viewStudent.course_name },
                    { label: 'Year', value: getYearDisplay(viewStudent.year) },
                    { label: 'Batch', value: viewStudent.batch_name },
                    { label: 'Session', value: viewStudent.session },
                    { label: 'Admission Year', value: viewStudent.admission_year }
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      padding: '0.75rem',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #e0f2fe'
                    }}>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '1rem', color: '#1f2937', fontWeight: 500 }}>
                        {item.value || 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#059669',
                  borderBottom: '2px solid #d1fae5',
                  paddingBottom: '0.5rem'
                }}>
                  📞 Contact Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                  {[
                    { label: 'Email', value: viewStudent.email },
                    { label: 'Phone Number', value: viewStudent.phone_number },
                    { label: 'Parent\'s Phone', value: viewStudent.parents_number },
                    { label: 'Present Address', value: viewStudent.present_address }
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      padding: '0.75rem',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '8px',
                      border: '1px solid #d1fae5',
                      ...(item.label === 'Present Address' ? { gridColumn: isMobile ? 'auto' : '1 / -1' } : {})
                    }}>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '1rem', color: '#1f2937', fontWeight: 500 }}>
                        {item.value || 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem'
            }}>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewStudent(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewStudent(null);
                  handleEditClick(viewStudent);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #64b5f6 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(25, 118, 210, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                ✏️ Edit Student
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminStudentManagement;